import { useState, useEffect } from "react";

/**
 * Custom hook to manage column visibility
 *
 * @param {String} tableId Unique identifier for the table to store preferences
 * @param {Array} columns Array of column configurations
 * @param {Array} defaultVisible Optional array of default visible column IDs
 * @returns {Object} Column visibility state and handlers
 */
export const useColumnVisibility = (
  tableId,
  columns,
  defaultVisible = null
) => {
  // Get all column IDs as the default if not specified
  const allColumnIds = columns.map((col) => col.id);
  const defaultVisibleColumns = defaultVisible || allColumnIds;

  // Try to load saved preferences from localStorage
  const loadSavedPreferences = () => {
    try {
      const saved = localStorage.getItem(`table_columns_${tableId}`);
      if (saved) {
        const parsedColumns = JSON.parse(saved);
        if (
          parsedColumns &&
          Array.isArray(parsedColumns) &&
          parsedColumns.length > 0
        ) {
          // Auto-include any new column IDs not in saved prefs
          const newIds = allColumnIds.filter((id) => !parsedColumns.includes(id));
          return newIds.length > 0
            ? [...parsedColumns, ...newIds].sort((a, b) => a - b)
            : parsedColumns;
        }
      }
    } catch (error) {
      console.error("Error loading saved column preferences:", error);
    }
    return defaultVisibleColumns;
  };

  const [visibleColumns, setVisibleColumns] = useState(loadSavedPreferences());

  // Save preferences to localStorage when they change
  useEffect(() => {
    try {
      // Never save an empty array of columns
      if (visibleColumns.length === 0) {
        setVisibleColumns(defaultVisibleColumns);
        return;
      }
      localStorage.setItem(
        `table_columns_${tableId}`,
        JSON.stringify(visibleColumns)
      );
    } catch (error) {
      console.error("Error saving column preferences:", error);
    }
  }, [visibleColumns, tableId, defaultVisibleColumns]);

  // Toggle a column's visibility
  const toggleColumnVisibility = (columnId) => {
    setVisibleColumns((prev) => {
      if (prev.includes(columnId)) {
        // Don't allow removing the last visible column
        if (prev.length === 1) {
          return prev;
        }
        return prev.filter((id) => id !== columnId);
      } else {
        return [...prev, columnId].sort((a, b) => a - b);
      }
    });
  };

  // Reset to default visibility
  const resetToDefault = () => {
    setVisibleColumns(defaultVisibleColumns);
    try {
      localStorage.removeItem(`table_columns_${tableId}`);
    } catch (error) {
      console.error("Error removing column preferences:", error);
    }
  };

  // Get the filtered visible columns
  const visibleColumnsList = columns.filter((col) =>
    visibleColumns.includes(col.id)
  );

  return {
    visibleColumns,
    setVisibleColumns,
    toggleColumnVisibility,
    resetToDefault,
    visibleColumnsList,
  };
};
